import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  ChevronLeft,
  ClipboardCheck,
  ClipboardCopy,
  Clock3,
  Eye,
  GraduationCap,
  Hash,
  Loader2,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  Tablet,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";

import { api } from "../../api";

import {
  cleanServiceNumber,
  formatServiceNumber,
  isValidServiceNumber,
} from "../../context/TrainingSessionContext";

import FirstClassAssessmentPanel from "./FirstClassAssessmentPanel";
import LeadingAssessmentPanel from "./LeadingAssessmentPanel";

import {
  FIRST_CLASS_TASKS,
} from "../../pages/site/firstclass";

import {
  LEADING_TASKS,
} from "../../pages/site/leading";

const ACTIVITY_LABELS = {
  newstarter: "New Starter",
  firstclass: "First Class",
  leading: "Leading Cadet",
};

const NEW_STARTER_TASKS = [
  {
    id: "task1",
    title: "About Your Squadron",
  },
  {
    id: "task2",
    title: "Cadet Portal",
  },
  {
    id: "task3",
    title: "Squadron Do’s and Don’ts",
  },
  {
    id: "task4",
    title: "Keeping Safe",
  },
  {
    id: "task5",
    title: "Health & Safety Induction",
  },
  {
    id: "task6",
    title: "Fire Safety Induction",
  },
  {
    id: "task7",
    title: "Initial Activities",
  },
  {
    id: "task8",
    title: "About Your Uniform",
  },
];

function formatDate(value) {
  if (!value) return "Never";

  try {
    return new Intl.DateTimeFormat(
      "en-GB",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    ).format(new Date(value));
  } catch {
    return value;
  }
}

function countTrueValues(value) {
  if (!value || typeof value !== "object") {
    return 0;
  }

  return Object.values(value).filter(Boolean).length;
}

function moduleMap(activity) {
  const result = {};

  for (const module of activity?.modules || []) {
    if (module?.module_id) {
      result[module.module_id] = module;
    }
  }

  return result;
}

function isReturnedAfterLatestSubmission(
  module,
  submittedAt
) {
  if (
    module?.review_outcome !== "returned"
  ) {
    return false;
  }

  const assessedAt =
    module?.assessed_at
      ? new Date(
          module.assessed_at
        ).getTime()
      : 0;

  const submittedTime =
    submittedAt
      ? new Date(
          submittedAt
        ).getTime()
      : 0;

  return submittedTime <= assessedAt;
}

function workbookMetrics(
  activity,
  tasks,
  submittedKey
) {
  const mandatoryTasks =
    tasks.filter(
      (task) => task.mandatory
    );

  const state =
    activity?.state || {};

  const submitted =
    state?.[submittedKey] || {};

  const modules =
    moduleMap(activity);

  let approved = 0;
  let awaiting = 0;
  let returned = 0;
  let started = 0;

  for (const task of mandatoryTasks) {
    const module =
      modules[task.id];

    const submittedAt =
      submitted[task.id];

    const isApproved =
      module?.status === "complete" ||
      module?.review_outcome ===
        "approved";

    const isReturned =
      isReturnedAfterLatestSubmission(
        module,
        submittedAt
      );

    if (isApproved) {
      approved += 1;
    } else if (isReturned) {
      returned += 1;
    } else if (submittedAt) {
      awaiting += 1;
    }

    if (
      submittedAt ||
      module?.status ===
        "in_progress" ||
      isApproved ||
      isReturned
    ) {
      started += 1;
    }
  }

  return {
    total:
      mandatoryTasks.length,
    approved,
    awaiting,
    returned,
    started,
    percent:
      mandatoryTasks.length
        ? Math.round(
            (approved /
              mandatoryTasks.length) *
              100
          )
        : 0,
  };
}

function newStarterMetrics(
  activity
) {
  const state =
    activity?.state || {};

  const stateCompleted =
    countTrueValues(
      state.completedTasks
    );

  const serverCompleted =
    Number(
      activity?.modules_complete ||
        0
    );

  const completed =
    Math.min(
      NEW_STARTER_TASKS.length,
      Math.max(
        stateCompleted,
        serverCompleted
      )
    );

  return {
    total:
      NEW_STARTER_TASKS.length,
    completed,
    percent: Math.round(
      (completed /
        NEW_STARTER_TASKS.length) *
        100
    ),
  };
}

function ProgressBar({
  value,
  className = "bg-raf-blue",
}) {
  return (
    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
      <div
        className={`h-full transition-all ${className}`}
        style={{
          width: `${Math.max(
            0,
            Math.min(100, value)
          )}%`,
        }}
      />
    </div>
  );
}

function StatChip({
  children,
  tone = "slate",
}) {
  const tones = {
    slate:
      "bg-slate-100 text-slate-700",
    blue:
      "bg-blue-100 text-blue-800",
    amber:
      "bg-amber-100 text-amber-900",
    red:
      "bg-red-100 text-red-800",
    green:
      "bg-emerald-100 text-emerald-800",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function AddExistingRecordModal({
  onClose,
  onCreated,
}) {
  const [value, setValue] =
    useState("");

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");

  const cleaned =
    cleanServiceNumber(value);

  const valid =
    isValidServiceNumber(
      cleaned
    );

  const submit = async (
    event
  ) => {
    event.preventDefault();

    setError("");

    if (!valid) {
      setError(
        "Enter a valid 13-digit 1471 service number, for example 3014710025001."
      );
      return;
    }

    setBusy(true);

    try {
      const { data } =
        await api.post(
          "/cadet-training/records",
          {
            service_number:
              cleaned,
          }
        );

      await onCreated(data);
    } catch (err) {
      setError(
        err?.response?.data
          ?.detail ||
          "Unable to add this service number."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-raf-navy text-white px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-white/60 uppercase tracking-widest">
              Existing cadet
            </p>

            <h2 className="text-xl font-bold mt-1">
              Add Issued Service
              Number
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>

        <form
          onSubmit={submit}
          className="p-5 md:p-6"
        >
          <p className="text-sm text-slate-600">
            Use this for a cadet
            who already has a 1471
            service number. The
            system creates a local
            training profile for
            that number without
            allocating a new one.
          </p>

          <label
            htmlFor="existing-service-number"
            className="block mt-5 text-sm font-bold text-raf-navy"
          >
            Service number
          </label>

          <input
            id="existing-service-number"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            value={formatServiceNumber(
              cleaned
            )}
            onChange={(event) =>
              setValue(
                cleanServiceNumber(
                  event.target.value
                )
              )
            }
            placeholder="30 1471 0025 001"
            className="mt-2 w-full border-2 border-slate-300 rounded-xl px-4 py-3 text-xl font-bold tracking-wider outline-none focus:border-raf-blue"
          />

          <p className="text-xs text-slate-500 mt-2">
            Format: 30 + 1471 +
            00YY + three-digit annual
            sequence.
          </p>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3 flex gap-2">
              <AlertTriangle
                size={18}
                className="text-red-600 shrink-0 mt-0.5"
              />

              <p className="text-sm text-red-800">
                {error}
              </p>
            </div>
          )}

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="border border-slate-300 rounded-lg px-4 py-2.5 font-semibold text-slate-700"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={
                busy || !valid
              }
              className="inline-flex items-center justify-center gap-2 bg-raf-navy text-white rounded-lg px-4 py-2.5 font-semibold disabled:opacity-50"
            >
              {busy ? (
                <Loader2
                  size={18}
                  className="animate-spin"
                />
              ) : (
                <UserPlus
                  size={18}
                />
              )}

              Add Existing Number
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewStarterDetail({
  activity,
}) {
  const state =
    activity?.state || {};

  const completedState =
    state.completedTasks ||
    {};

  const modules =
    moduleMap(activity);

  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">
        New Starter tasks
      </p>

      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        {NEW_STARTER_TASKS.map(
          (task) => {
            const done =
              Boolean(
                completedState[
                  task.id
                ]
              ) ||
              modules[
                task.id
              ]?.status ===
                "complete";

            return (
              <div
                key={task.id}
                className={[
                  "rounded-lg border p-3 flex items-start gap-2",
                  done
                    ? "border-emerald-200 bg-emerald-50"
                    : "border-slate-200 bg-slate-50",
                ].join(" ")}
              >
                {done ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                ) : (
                  <Clock3 className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                )}

                <div>
                  <p className="text-xs text-slate-500 font-bold">
                    {task.id
                      .replace(
                        "task",
                        "Task "
                      )}
                  </p>

                  <p className="text-sm font-semibold text-raf-navy">
                    {task.title}
                  </p>
                </div>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
}

function TrainingStageCard({
  title,
  subtitle,
  progressText,
  percent,
  lastSaved,
  chips = [],
  actionLabel,
  onAction,
  actionDisabled = false,
  children = null,
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h3 className="text-xl font-black text-raf-navy">
              {title}
            </h3>

            <p className="text-sm text-slate-500 mt-1">
              {subtitle}
            </p>
          </div>

          <p className="text-xl font-black text-raf-navy">
            {progressText}
          </p>
        </div>

        <div className="mt-4">
          <ProgressBar
            value={percent}
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {chips.map(
            (chip, index) => (
              <StatChip
                key={`${chip.label}-${index}`}
                tone={
                  chip.tone
                }
              >
                {chip.label}
              </StatChip>
            )
          )}
        </div>

        {lastSaved && (
          <p className="text-xs text-slate-400 mt-3">
            Last saved{" "}
            {formatDate(
              lastSaved
            )}
          </p>
        )}

        {children}

        {onAction && (
          <button
            type="button"
            onClick={onAction}
            disabled={
              actionDisabled
            }
            className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-raf-navy text-white px-4 py-2.5 font-bold disabled:opacity-50"
          >
            {actionLabel}
            <ArrowRight
              size={17}
            />
          </button>
        )}
      </div>
    </div>
  );
}

function CadetRecordOverview({
  serviceNumber,
  onBack,
  onOpenAssessment,
  onReset,
  onDelete,
}) {
  const [record, setRecord] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [showNewStarter, setShowNewStarter] =
    useState(false);

  const [deleting, setDeleting] =
    useState(false);

  const load = useCallback(
    async () => {
      setLoading(true);
      setError("");

      try {
        const { data } =
          await api.get(
            `/cadet-training/records/${serviceNumber}`
          );

        setRecord(data);
      } catch (err) {
        setError(
          err?.response?.data
            ?.detail ||
            "Unable to load this training record."
        );
      } finally {
        setLoading(false);
      }
    },
    [serviceNumber]
  );

  useEffect(() => {
    load();
  }, [load]);

  const newStarter =
    newStarterMetrics(
      record?.activities
        ?.newstarter
    );

  const firstClass =
    workbookMetrics(
      record?.activities
        ?.firstclass,
      FIRST_CLASS_TASKS,
      "firstClassSubmitted"
    );

  const leading =
    workbookMetrics(
      record?.activities
        ?.leading,
      LEADING_TASKS,
      "leadingSubmitted"
    );

  const awaitingTotal =
    firstClass.awaiting +
    leading.awaiting;

  const returnedTotal =
    firstClass.returned +
    leading.returned;

  const resetRecord = async () => {
    const confirmed =
      window.confirm(
        `Reset all training progress for ${formatServiceNumber(
          serviceNumber
        )}?\n\nThe service number will remain allocated, but all New Starter, First Class and Leading progress will be cleared.`
      );

    if (!confirmed) {
      return;
    }

    try {
      await onReset(
        serviceNumber
      );

      await load();
    } catch {
      // Parent panel exposes the error.
    }
  };

  const deleteRecord = async () => {
    const confirmed =
      window.confirm(
        `PERMANENTLY DELETE ${formatServiceNumber(
          serviceNumber
        )}?\n\nUse this only when the cadet has left and this local training profile is no longer required.\n\nAll saved training progress will be deleted. This cannot be undone.`
      );

    if (!confirmed) {
      return;
    }

    setDeleting(true);

    try {
      await onDelete(
        serviceNumber
      );

      onBack();
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-raf-navy" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-raf-navy font-semibold"
        >
          <ChevronLeft
            size={18}
          />
          Back to Training
          Records
        </button>

        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="bg-raf-navy text-white rounded-xl overflow-hidden border-b-4 border-raf-red">
        <div className="p-5 md:p-6">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white"
          >
            <ChevronLeft
              size={17}
            />
            Training Records
          </button>

          <div className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/55 font-bold">
                Cadet training record
              </p>

              <h1 className="mt-1 text-3xl md:text-4xl font-black tracking-wider">
                {formatServiceNumber(
                  record.service_number
                )}
              </h1>

              <p className="text-white/65 text-sm mt-2">
                One service number
                across New Starter,
                First Class and
                Leading Cadet.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {awaitingTotal > 0 && (
                <StatChip tone="amber">
                  {awaitingTotal} awaiting
                  assessment
                </StatChip>
              )}

              {returnedTotal > 0 && (
                <StatChip tone="red">
                  {returnedTotal} returned
                </StatChip>
              )}

              {awaitingTotal ===
                0 &&
                returnedTotal ===
                  0 && (
                  <StatChip tone="green">
                    No marking waiting
                  </StatChip>
                )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
            Created
          </p>

          <p className="font-bold text-raf-navy mt-1">
            {formatDate(
              record.created_at
            )}
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
            Last training use
          </p>

          <p className="font-bold text-raf-navy mt-1">
            {formatDate(
              record.last_seen_at
            )}
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
            Last activity
          </p>

          <p className="font-bold text-raf-navy mt-1">
            {record.last_activity
              ? ACTIVITY_LABELS[
                  record
                    .last_activity
                ] ||
                record
                  .last_activity
              : "Not yet used"}
          </p>
        </div>
      </div>

      <TrainingStageCard
        title="New Starter"
        subtitle="Initial induction and squadron familiarisation."
        progressText={`${newStarter.completed} / ${newStarter.total}`}
        percent={
          newStarter.percent
        }
        lastSaved={
          record?.activities
            ?.newstarter
            ?.last_saved_at
        }
        chips={[
          {
            label:
              newStarter.completed ===
              newStarter.total
                ? "Complete"
                : `${newStarter.total - newStarter.completed} remaining`,
            tone:
              newStarter.completed ===
              newStarter.total
                ? "green"
                : "blue",
          },
        ]}
        actionLabel={
          showNewStarter
            ? "Hide New Starter Detail"
            : "View New Starter Detail"
        }
        onAction={() =>
          setShowNewStarter(
            (previous) =>
              !previous
          )
        }
      >
        {showNewStarter && (
          <NewStarterDetail
            activity={
              record?.activities
                ?.newstarter
            }
          />
        )}
      </TrainingStageCard>

      <TrainingStageCard
        title="First Class"
        subtitle="Mandatory Pass activities plus optional Merit and Distinction work."
        progressText={`${firstClass.approved} / ${firstClass.total} approved`}
        percent={
          firstClass.percent
        }
        lastSaved={
          record?.activities
            ?.firstclass
            ?.last_saved_at
        }
        chips={[
          {
            label: `${firstClass.awaiting} awaiting assessment`,
            tone:
              firstClass.awaiting
                ? "amber"
                : "slate",
          },
          {
            label: `${firstClass.returned} returned`,
            tone:
              firstClass.returned
                ? "red"
                : "slate",
          },
          {
            label: `${firstClass.started} started`,
            tone: "blue",
          },
        ]}
        actionLabel="Open First Class Workbook"
        onAction={() =>
          onOpenAssessment(
            "firstclass",
            serviceNumber
          )
        }
      />

      <TrainingStageCard
        title="Leading Cadet"
        subtitle="Essential Knowledge plus optional Develop Understanding activities."
        progressText={`${leading.approved} / ${leading.total} approved`}
        percent={
          leading.percent
        }
        lastSaved={
          record?.activities
            ?.leading
            ?.last_saved_at
        }
        chips={[
          {
            label: `${leading.awaiting} awaiting assessment`,
            tone:
              leading.awaiting
                ? "amber"
                : "slate",
          },
          {
            label: `${leading.returned} returned`,
            tone:
              leading.returned
                ? "red"
                : "slate",
          },
          {
            label: `${leading.started} started`,
            tone: "blue",
          },
        ]}
        actionLabel="Open Leading Notebook"
        onAction={() =>
          onOpenAssessment(
            "leading",
            serviceNumber
          )
        }
      />

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
        <ShieldCheck className="w-5 h-5 text-blue-800 shrink-0 mt-0.5" />

        <div>
          <p className="font-bold text-blue-950">
            Electronic training
            record
          </p>

          <p className="text-sm text-blue-900 mt-1">
            Instructor approvals and
            feedback are stored
            against this service
            number. They follow the
            cadet to any authorised
            training tablet.
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-black text-raf-navy">
          Record administration
        </h3>

        <div className="mt-4 grid md:grid-cols-2 gap-3">
          <div className="border border-amber-200 bg-amber-50 rounded-xl p-4">
            <h4 className="font-bold text-amber-950">
              Reset progress
            </h4>

            <p className="text-sm text-amber-900 mt-1">
              Clears all local
              training work while
              keeping the service
              number allocated.
            </p>

            <button
              type="button"
              onClick={resetRecord}
              className="mt-3 inline-flex items-center gap-2 border border-amber-400 bg-white text-amber-900 rounded-lg px-4 py-2.5 text-sm font-semibold"
            >
              <RotateCcw
                size={16}
              />
              Reset Progress
            </button>
          </div>

          <div className="border border-red-200 bg-red-50 rounded-xl p-4">
            <h4 className="font-bold text-red-950">
              Cadet has left
            </h4>

            <p className="text-sm text-red-900 mt-1">
              Permanently removes
              this local training
              profile and all saved
              progress.
            </p>

            <button
              type="button"
              onClick={
                deleteRecord
              }
              disabled={
                deleting
              }
              className="mt-3 inline-flex items-center gap-2 bg-red-700 text-white rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
            >
              {deleting ? (
                <Loader2
                  size={16}
                  className="animate-spin"
                />
              ) : (
                <Trash2
                  size={16}
                />
              )}

              Delete Profile
            </button>
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-3">
          Deleted service numbers
          are not automatically
          re-issued.
        </p>
      </div>
    </div>
  );
}

export const CadetTrainingRecordsPanel =
  () => {
    const currentYear =
      new Date().getFullYear();

    const [records, setRecords] =
      useState([]);

    const [loading, setLoading] =
      useState(true);

    const [error, setError] =
      useState("");

    const [search, setSearch] =
      useState("");

    const [year, setYear] =
      useState(currentYear);

    const [allocating, setAllocating] =
      useState(false);

    const [selectedRecord, setSelectedRecord] =
      useState(null);

    const [allocatedRecord, setAllocatedRecord] =
      useState(null);

    const [copiedNumber, setCopiedNumber] =
      useState("");

    const [showAddExisting, setShowAddExisting] =
      useState(false);

    const [assessmentMode, setAssessmentMode] =
      useState("");

    const [assessmentServiceNumber, setAssessmentServiceNumber] =
      useState("");

    const [quickOpen, setQuickOpen] =
      useState("");

    const [quickOpenError, setQuickOpenError] =
      useState("");

    const loadRecords =
      useCallback(
        async () => {
          setLoading(true);
          setError("");

          try {
            const { data } =
              await api.get(
                "/cadet-training/records"
              );

            setRecords(
              Array.isArray(data)
                ? data
                : []
            );
          } catch (err) {
            setError(
              err?.response?.data
                ?.detail ||
                "Unable to load cadet training records."
            );
          } finally {
            setLoading(false);
          }
        },
        []
      );

    useEffect(() => {
      loadRecords();
    }, [loadRecords]);

    const allocateRecord =
      async () => {
        const confirmed =
          window.confirm(
            `Allocate the next 1471 service number for ${year}?`
          );

        if (!confirmed) {
          return;
        }

        setAllocating(true);
        setError("");

        try {
          const { data } =
            await api.post(
              "/cadet-training/records/allocate",
              {
                year:
                  Number(year),
              }
            );

          setAllocatedRecord(
            data.record
          );

          await loadRecords();
        } catch (err) {
          setError(
            err?.response?.data
              ?.detail ||
              "Unable to allocate a service number."
          );
        } finally {
          setAllocating(false);
        }
      };

    const resetRecord =
      async (
        serviceNumber
      ) => {
        setError("");

        try {
          await api.post(
            `/cadet-training/records/${serviceNumber}/reset`
          );

          await loadRecords();
        } catch (err) {
          const message =
            err?.response?.data
              ?.detail ||
            "Unable to reset the training record.";

          setError(message);

          throw err;
        }
      };

    const deleteRecord =
      async (
        serviceNumber
      ) => {
        setError("");

        try {
          await api.delete(
            `/cadet-training/records/${serviceNumber}`
          );

          await loadRecords();
        } catch (err) {
          const message =
            err?.response?.data
              ?.detail ||
            "Unable to delete the training profile.";

          setError(message);

          throw err;
        }
      };

    const copyNumber =
      async (number) => {
        try {
          await navigator.clipboard.writeText(
            number
          );

          setCopiedNumber(
            number
          );

          window.setTimeout(
            () =>
              setCopiedNumber(
                ""
              ),
            1600
          );
        } catch {
          setError(
            "Unable to copy the service number."
          );
        }
      };

    const openRecord =
      (number) => {
        const cleaned =
          cleanServiceNumber(
            number
          );

        if (
          !isValidServiceNumber(
            cleaned
          )
        ) {
          setQuickOpenError(
            "Enter a valid 1471 service number."
          );

          return;
        }

        const exists =
          records.some(
            (record) =>
              record.service_number ===
              cleaned
          );

        if (!exists) {
          setQuickOpenError(
            "No local training record exists for that service number."
          );

          return;
        }

        setQuickOpenError("");
        setSelectedRecord(
          cleaned
        );
      };

    const openAssessment =
      (
        mode,
        number
      ) => {
        setAssessmentServiceNumber(
          number
        );

        setAssessmentMode(
          mode
        );
      };

    const filteredRecords =
      useMemo(() => {
        const query =
          search
            .replace(
              /\s/g,
              ""
            )
            .trim();

        return [
          ...records,
        ]
          .filter(
            (record) => {
              if (!query) {
                return true;
              }

              return String(
                record.service_number ||
                  ""
              ).includes(
                query
              );
            }
          )
          .sort(
            (a, b) => {
              if (
                b.enrolment_year !==
                a.enrolment_year
              ) {
                return (
                  b.enrolment_year -
                  a.enrolment_year
                );
              }

              return (
                Number(
                  b.sequence ||
                    0
                ) -
                Number(
                  a.sequence ||
                    0
                )
              );
            }
          );
      }, [
        records,
        search,
      ]);

    const thisYearCount =
      records.filter(
        (record) =>
          Number(
            record.enrolment_year
          ) ===
          currentYear
      ).length;

    const usedCount =
      records.filter(
        (record) =>
          Boolean(
            record.last_seen_at
          )
      ).length;

    if (
      assessmentMode ===
      "firstclass"
    ) {
      return (
        <FirstClassAssessmentPanel
          initialServiceNumber={
            assessmentServiceNumber
          }
          onClose={() => {
            setAssessmentMode(
              ""
            );

            setAssessmentServiceNumber(
              ""
            );

            loadRecords();
          }}
        />
      );
    }

    if (
      assessmentMode ===
      "leading"
    ) {
      return (
        <LeadingAssessmentPanel
          initialServiceNumber={
            assessmentServiceNumber
          }
          onClose={() => {
            setAssessmentMode(
              ""
            );

            setAssessmentServiceNumber(
              ""
            );

            loadRecords();
          }}
        />
      );
    }

    if (selectedRecord) {
      return (
        <CadetRecordOverview
          serviceNumber={
            selectedRecord
          }
          onBack={() => {
            setSelectedRecord(
              null
            );

            loadRecords();
          }}
          onOpenAssessment={
            openAssessment
          }
          onReset={
            resetRecord
          }
          onDelete={
            deleteRecord
          }
        />
      );
    }

    return (
      <div className="space-y-5">
        <div className="bg-white border border-slate-200 rounded-xl p-5 md:p-6">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-raf-navy text-white rounded-xl flex items-center justify-center">
                <GraduationCap
                  size={23}
                />
              </div>

              <div>
                <h1 className="font-display font-bold text-2xl text-raf-navy">
                  Cadet Training
                  Records
                </h1>

                <p className="text-sm text-raf-slate mt-1">
                  Open one service
                  number to view the
                  cadet's complete
                  local training
                  record.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() =>
                  setShowAddExisting(
                    true
                  )
                }
                className="inline-flex items-center justify-center gap-2 border border-raf-blue text-raf-blue hover:bg-raf-blue hover:text-white rounded-lg px-4 py-2.5 font-semibold transition-colors"
              >
                <UserPlus
                  size={18}
                />
                Add Existing
                Number
              </button>

              <input
                type="number"
                min="2000"
                max="2099"
                value={year}
                onChange={(
                  event
                ) =>
                  setYear(
                    event.target.value
                  )
                }
                className="border border-slate-300 rounded-lg px-3 py-2.5 w-full sm:w-28"
                aria-label="Enrolment year"
              />

              <button
                type="button"
                onClick={
                  allocateRecord
                }
                disabled={
                  allocating
                }
                className="inline-flex items-center justify-center gap-2 bg-raf-red hover:bg-[#A00926] text-white rounded-lg px-4 py-2.5 font-semibold disabled:opacity-50"
              >
                {allocating ? (
                  <Loader2
                    size={18}
                    className="animate-spin"
                  />
                ) : (
                  <Plus
                    size={18}
                  />
                )}

                Allocate Next
                Number
              </button>
            </div>
          </div>
        </div>

        <div className="bg-raf-navy text-white rounded-xl p-5 md:p-6 border-b-4 border-raf-red">
          <div className="flex items-start gap-3">
            <BookOpen className="w-7 h-7 shrink-0" />

            <div className="flex-1">
              <h2 className="text-xl font-black">
                Open Cadet Training
                Record
              </h2>

              <p className="text-sm text-white/70 mt-1">
                Enter the service
                number once to see
                New Starter, First
                Class and Leading
                progress together.
              </p>

              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  spellCheck={false}
                  value={formatServiceNumber(
                    cleanServiceNumber(
                      quickOpen
                    )
                  )}
                  onChange={(
                    event
                  ) => {
                    setQuickOpen(
                      cleanServiceNumber(
                        event.target.value
                      )
                    );

                    setQuickOpenError(
                      ""
                    );
                  }}
                  onKeyDown={(
                    event
                  ) => {
                    if (
                      event.key ===
                      "Enter"
                    ) {
                      openRecord(
                        quickOpen
                      );
                    }
                  }}
                  placeholder="30 1471 0026 001"
                  className="flex-1 rounded-xl border-2 border-white/30 bg-white text-slate-900 px-4 py-3 text-xl font-black tracking-wider outline-none focus:border-white"
                />

                <button
                  type="button"
                  onClick={() =>
                    openRecord(
                      quickOpen
                    )
                  }
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-raf-red px-5 py-3 font-black text-white"
                >
                  <Eye
                    size={18}
                  />
                  Open Record
                </button>
              </div>

              {quickOpenError && (
                <p className="text-sm text-red-200 mt-3 font-semibold">
                  {quickOpenError}
                </p>
              )}
            </div>
          </div>
        </div>

        {allocatedRecord && (
          <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-5">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex gap-3">
                <CheckCircle2 className="text-emerald-700 shrink-0" />

                <div>
                  <p className="font-bold text-emerald-900">
                    Service number
                    added
                  </p>

                  <p className="text-2xl md:text-3xl font-black tracking-wider text-raf-navy mt-2">
                    {formatServiceNumber(
                      allocatedRecord.service_number
                    )}
                  </p>

                  <p className="text-sm text-emerald-800 mt-2">
                    The local
                    training profile
                    contains no cadet
                    name.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() =>
                    copyNumber(
                      allocatedRecord.service_number
                    )
                  }
                  className="inline-flex items-center justify-center gap-2 border border-emerald-400 bg-white rounded-lg px-4 py-2.5 font-semibold text-emerald-800"
                >
                  {copiedNumber ===
                  allocatedRecord.service_number ? (
                    <Check
                      size={17}
                    />
                  ) : (
                    <ClipboardCopy
                      size={17}
                    />
                  )}

                  {copiedNumber ===
                  allocatedRecord.service_number
                    ? "Copied"
                    : "Copy Number"}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setSelectedRecord(
                      allocatedRecord.service_number
                    )
                  }
                  className="inline-flex items-center justify-center gap-2 bg-raf-navy text-white rounded-lg px-4 py-2.5 font-semibold"
                >
                  <Eye
                    size={17}
                  />
                  Open Record
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-3">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Hash className="text-raf-blue" />

              <div>
                <p className="text-2xl font-black text-raf-navy">
                  {
                    records.length
                  }
                </p>

                <p className="text-xs uppercase tracking-wider text-slate-500">
                  Active profiles
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <GraduationCap className="text-raf-blue" />

              <div>
                <p className="text-2xl font-black text-raf-navy">
                  {
                    thisYearCount
                  }
                </p>

                <p className="text-xs uppercase tracking-wider text-slate-500">
                  {currentYear}{" "}
                  numbers
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Tablet className="text-raf-blue" />

              <div>
                <p className="text-2xl font-black text-raf-navy">
                  {
                    usedCount
                  }
                </p>

                <p className="text-xs uppercase tracking-wider text-slate-500">
                  Training started
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="search"
                value={search}
                onChange={(
                  event
                ) =>
                  setSearch(
                    event.target.value
                  )
                }
                placeholder="Search existing records..."
                className="w-full border border-slate-300 rounded-lg pl-10 pr-4 py-2.5 outline-none focus:border-raf-blue"
              />
            </div>

            <button
              type="button"
              onClick={
                loadRecords
              }
              className="inline-flex items-center justify-center gap-2 border border-slate-300 rounded-lg px-4 py-2.5 text-raf-navy font-semibold hover:bg-slate-50"
            >
              <RefreshCw
                size={17}
              />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
            <AlertTriangle className="text-red-600 shrink-0" />

            <p className="text-sm text-red-800">
              {error}
            </p>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="py-16 flex justify-center">
              <Loader2 className="animate-spin text-raf-navy" />
            </div>
          ) : filteredRecords.length ===
            0 ? (
            <div className="py-14 px-5 text-center">
              <GraduationCap className="mx-auto text-slate-300 w-12 h-12" />

              <h3 className="font-bold text-raf-navy mt-3">
                No training
                records found
              </h3>

              <p className="text-sm text-slate-500 mt-1">
                Add an existing
                service number or
                allocate a new one.
              </p>
            </div>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-500">
                        Service number
                      </th>

                      <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-500">
                        Year
                      </th>

                      <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-500">
                        Status
                      </th>

                      <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-500">
                        Last activity
                      </th>

                      <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-slate-500">
                        Last used
                      </th>

                      <th className="text-right px-5 py-3">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {filteredRecords.map(
                      (record) => (
                        <tr
                          key={
                            record.service_number
                          }
                          className="hover:bg-slate-50"
                        >
                          <td className="px-5 py-4">
                            <div className="font-bold text-raf-navy tracking-wide">
                              {formatServiceNumber(
                                record.service_number
                              )}
                            </div>
                          </td>

                          <td className="px-5 py-4 text-slate-600">
                            {
                              record.enrolment_year
                            }
                          </td>

                          <td className="px-5 py-4">
                            {record.last_seen_at ? (
                              <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-blue-100 text-blue-800 px-2.5 py-1 rounded-full">
                                <Clock3
                                  size={13}
                                />
                                Training
                                started
                              </span>
                            ) : (
                              <span className="inline-flex text-xs font-bold bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                                Not started
                              </span>
                            )}
                          </td>

                          <td className="px-5 py-4 text-slate-600">
                            {record.last_activity
                              ? ACTIVITY_LABELS[
                                  record
                                    .last_activity
                                ] ||
                                record
                                  .last_activity
                              : "—"}
                          </td>

                          <td className="px-5 py-4 text-slate-500">
                            {formatDate(
                              record.last_seen_at
                            )}
                          </td>

                          <td className="px-5 py-4">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  copyNumber(
                                    record.service_number
                                  )
                                }
                                className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                                title="Copy service number"
                              >
                                {copiedNumber ===
                                record.service_number ? (
                                  <Check
                                    size={16}
                                    className="text-emerald-600"
                                  />
                                ) : (
                                  <ClipboardCopy
                                    size={16}
                                  />
                                )}
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  setSelectedRecord(
                                    record.service_number
                                  )
                                }
                                className="inline-flex items-center gap-1.5 bg-raf-navy text-white rounded-lg px-3 py-2 font-semibold"
                              >
                                <Eye
                                  size={15}
                                />
                                Open Record
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden divide-y divide-slate-100">
                {filteredRecords.map(
                  (record) => (
                    <button
                      type="button"
                      key={
                        record.service_number
                      }
                      onClick={() =>
                        setSelectedRecord(
                          record.service_number
                        )
                      }
                      className="w-full text-left p-4 hover:bg-slate-50"
                    >
                      <div className="font-black text-raf-navy text-lg tracking-wide">
                        {formatServiceNumber(
                          record.service_number
                        )}
                      </div>

                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        <span className="bg-slate-100 rounded-full px-2.5 py-1 text-slate-600">
                          {
                            record.enrolment_year
                          }
                        </span>

                        <span
                          className={`rounded-full px-2.5 py-1 font-semibold ${
                            record.last_seen_at
                              ? "bg-blue-100 text-blue-800"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {record.last_seen_at
                            ? "Training started"
                            : "Not started"}
                        </span>
                      </div>

                      {record.last_seen_at && (
                        <p className="text-xs text-slate-500 mt-2">
                          Last used{" "}
                          {formatDate(
                            record.last_seen_at
                          )}
                        </p>
                      )}
                    </button>
                  )
                )}
              </div>
            </>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-900">
            Existing cadets keep the
            service number already
            issued to them. New
            cadets can be given the
            next automatically
            allocated number.
            Profiles can be deleted
            when a cadet leaves.
          </p>
        </div>

        {showAddExisting && (
          <AddExistingRecordModal
            onClose={() =>
              setShowAddExisting(
                false
              )
            }
            onCreated={async (
              record
            ) => {
              setAllocatedRecord(
                record
              );

              setShowAddExisting(
                false
              );

              await loadRecords();
            }}
          />
        )}
      </div>
    );
  };

export default CadetTrainingRecordsPanel;